import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { from } from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import IconWarningBlack from "../../images/icon_warning_black.svg";
import { HTTP_METHODS, REQUEST_STATES } from "../../utils/constants";
import { ENVIRONMENT_NAMES, getEnvironment } from "../../utils/deploymentUtils";
import { ERROR_MESSAGES } from "../../utils/exceptions";
import { networkBackendRequest } from "../../utils/networkUtils";
import BillingSearchBar, {
  INITIAL_FILTERS,
} from "../BillingSearchBar/BillingSearchBar";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { ConfirmationModalList } from "../ConfirmationModalList/ConfirmationModalList";
import DamLoader from "../DamLoader/DamLoader";
import Dams from "../Dams/Dams";
import QuotationConfirmation from "../QuotationConfirmation/QuotationConfirmation";
import { BatchPrefactura } from "./BatchPrefactura";
import {
  ButtonContainer,
  DivMessage,
  InstructionsLabel,
  MainButton,
  StyledTag,
} from "./BillingScreenStyles";

export const statusesToVerifyDam = [
  "electronic_mandate_status",
  "senasa_status",
  "ace_block_status",
  "levante_status",
];

const statusesToVerify = [
  "sitradi_block_status",
  "payment_status_deadline",
  "sitradi_immobilization_status",
  "handling_payment_status",
];

export const extractDamVolanteStatus = (volante, statusesToVerify) => {
  const statuses = statusesToVerify.map((key) => ({ ...volante[key], key }));
  const problems = statuses.filter((status) => status.category === "problem");
  const warnings = statuses.filter((status) => status.category === "warning");
  return { problems, warnings };
};

const convertEstimateDamResponse = (dni: string) => (damData) => {
  const warningData = damData?.warning;
  damData = damData.dam;
  const status = extractDamVolanteStatus(damData, statusesToVerifyDam);
  const filteredDam = {
    status,
    ...damData,
    id: damData.dam_num,
    warning: warningData,
    consigneeName: damData.consignee_name,
    senasaStatus: damData.senasa_status,
    levanteStatus: damData.levante_status,
    aceBlockStatus: damData.ace_block_status,
    electronicMandateStatus: damData.electronic_mandate_status,
    entryDate: damData.entry_date,
    paidVolantes: `${damData.paid_volantes_num}/${damData.volantes_num} volantes`,
    amount: damData.amount,
    senasaComment: damData.senasa_rejected_comment,
    electronicMandateComment: damData.electronic_mandate_rejected_comment,
    dispatchTypeCode: damData.despacho_type_code,
    volantes: (damData.volantes as any[]).map((volanteData) => {
      const status = extractDamVolanteStatus(volanteData, statusesToVerify);
      return {
        status2: status,
        ...volanteData,
        dni,
        id: volanteData.volante_num,
        importer: volanteData.consignee_name,
        weight: volanteData.parcels_weight,
        parcels: volanteData.parcels_num,
        days: volanteData.days_stored,
        airline: volanteData.airline,
        flightDate: volanteData.flight_date,
        masterAirWaybill: volanteData.master_air_waybill,
        houseAirWaybill: volanteData.house_air_waybill,
        status: getVolanteStatus([
          volanteData.payment_status,
          volanteData.sitradi_block_status,
          volanteData.sitradi_immobilization_status,
        ]),
        sitradiBlockStatus: volanteData.sitradi_block_status,
        sitradiImmobilizationStatus: volanteData.sitradi_immobilization_status,
        additionalChargesStatus: volanteData.additional_charges_status,
        paymentStatus: volanteData.payment_status,
        rucs: volanteData.billable_ruc_options,
        damId: damData.dam_num,
        detailsOptions: [],
        thirdPartyStatus: volanteData.third_party_billing_authorization_status,

        billableRuc: volanteData.billable_ruc,
        withdrawalDate: volanteData.withdrawal_date,
        salesPriceTotal: volanteData.sales_price_total,
        handlingPaymentStatus: volanteData.handling_payment_status,
        sitradiAceBlockStatus: volanteData.sitradi_ace_block_status,
        entryDate: volanteData.entry_date,
        contentDescription: volanteData.content_description,
      };
    }),
  };
  return filteredDam;
};

const CATEGORIES = {
  PROBLEM: "problem",
  OK: "ok",
  WARNING: "warning",
};

function getVolanteStatus(listOfStatus) {
  const problemStatus = listOfStatus.filter(
    (status) => status.category === CATEGORIES.PROBLEM
  );

  if (problemStatus.length > 0) {
    return problemStatus[0];
  }

  const warningStatus = listOfStatus.filter(
    (status) => status.category === CATEGORIES.WARNING
  );

  if (warningStatus.length > 0) {
    return warningStatus[0];
  }

  return listOfStatus[0];
}

export const getErrorValue = (eResponse) => {
  if (
    eResponse.translated_error_message &&
    eResponse.translated_error_message.length > 0
  ) {
    return eResponse.translated_error_message;
  }
  const errorCode = eResponse?.error_code;
  return errorCode in ERROR_MESSAGES
    ? ERROR_MESSAGES[errorCode].MSG
    : ERROR_MESSAGES.DEFAULT.MSG;
};

export interface IEstimateDamData {
  document_number: string;
  withdrawal_date: string;
  dam_num: string;
  volantes: {
    additional_services: any[];
    volante_num: string;
  }[];
}
export interface IEstimateDamResponse extends IEstimateDamData {
  dam_response: any;
}

export const getVolantePricing = (params: IEstimateDamData) => {
  return networkBackendRequest(
    "/volantes/quotation-estimate/",
    HTTP_METHODS.POST,
    null,
    params,
    null,
    true
  );
};

function BillingScreen(props) {
  const { userInfo } = props;
  const [volantesForInvoice, updateVolantesForInvoice] = useState([]);
  const [showModal, updateShowModal] = useState(false);
  const [searchError, updateSearchError] = useState(false);
  const [selectedVolantes, updateSelectedVolantes] = useState([]);
  const [isButtonDisabled, updateIsButtonDisabled] = useState(true);
  const [errors, setErrors] = useState<string>("");
  const [estimateProcess, setEstimateProcess] = useState([]);

  const dniState = useState(INITIAL_FILTERS.dni);
  const [dni, setDni] = dniState;

  const volantesForRequestState = useState(INITIAL_FILTERS.volantesForRequest);
  const [, updateVolantesForRequest] = volantesForRequestState;

  const updateDamSuffixState = useState(INITIAL_FILTERS.damSuffix);

  const [, updateDamSuffix] = updateDamSuffixState;

  const isProd = getEnvironment() === ENVIRONMENT_NAMES.PRD;

  const [showModalWarning, setShowModalWarning] = useState(false);
  const [messageWarning, setMessageWarning] = useState("");

  const [showTypeModalError, setShowTypeModalError] = useState<
    "list" | "general" | ""
  >("");

  // const estimateDam = useAsyncExecutor2(
  //   (params: IEstimateDamData) => {
  //     const { dam_num, document_number } = params;
  //     const volante_nums = params.volantes.map((v) => v.volante_num);
  //     const requestDam = networkBackendRequest(
  //       `/dams/${dam_num}/estimate/intranet`,
  //       HTTP_METHODS.GET,
  //       {
  //         dam_num,
  //         volante_nums,
  //       }
  //     )
  //       .then((resp) => resp.json())
  //       .then(convertEstimateDamResponse(document_number));

  //     const requestVolantes = getVolantePricing(params);
  //     return zip(
  //       [from(requestDam).pipe(), from(requestVolantes)],
  //       (dam: any, respVolantePricings) => {
  //         const volantes = dam.volantes.map((volante) => {
  //           const found: any = respVolantePricings.quotation_data.find(
  //             (volatePricing: any) => {
  //               return volatePricing.volante_num === volante.volante_num;
  //             }
  //           );
  //           return { ...volante, pricing: { ...found, dam_num } };
  //         });
  //         return [{ ...dam, volantes }];
  //       }
  //     );
  //   },

  //   {
  //     sequential: "SWITCH",
  //     retry: {
  //       interval: 1000,
  //       maxRetryAttempts: 1,
  //     },
  //     onProcessing: () => {
  //       setDamStatus("PROCESSING");
  //     },
  //     onSuccess: (resp: any, params) => {
  //       setDamStatus("SUCCESS");
  //       setDamNumnber(resp[0].id);
  //       resp[0].warning = { "warning_code": "WDAM002", "warning_message": "El número de manifiesto del volante no corresponde al número de manifiesto declarado", "warning_block": true }
  //       if (resp[0].warning) {
  //         if (resp[0].warning?.warning_block == true) {
  //           console.log("dam.warning?.warning_message", resp[0].warning?.warning_message);
  //           setShowModalWarning(true);
  //           setMessageWarning(resp[0].warning?.warning_message);
  //         return;
  //         }
  //       }
  //       setDni(params.document_number);
  //     },
  //     onError: (error) => {
  //       const { modal_type } = error;
  //       setDamStatus("FAILED");
  //       setShowTypeModalError(modal_type ? modal_type : "general");
  //       const error_mesagge = getErrorValue(error);
  //       setErrors(error_mesagge);
  //     },
  //   }
  // );

  const handleError = useCallback(
    (modalType: "list" | "general", error: Error) => {
      setDamStatus("FAILED");
      setShowTypeModalError(modalType ? modalType : "general");
      const error_mesagge = getErrorValue(error);
      setErrors(error_mesagge);
    },
    []
  );

  const processEstimate = useAsyncExecutor2(
    (params: IEstimateDamResponse) => {
      const requestVolantes = getVolantePricing(params);
      return from(requestVolantes);
    },
    {
      sequential: "SWITCH",
      retry: {
        interval: 1000,
        maxRetryAttempts: 1,
      },
      onProcessing: () => {
        setDamStatus("PROCESSING");
      },
      onSuccess: (respVolantePricings: any, params) => {
        const dam_num = params.dam_num;
        const dam = params.dam_response;

        if (
          !respVolantePricings.quotation_data ||
          respVolantePricings.quotation_data.length === 0
        ) {
          handleError("general", new Error("Error processing estimate"));
          setEstimateProcess([]);
          return;
        }

        const volantes = dam.volantes.map((volante) => {
          const found: any = respVolantePricings.quotation_data.find(
            (volatePricing: any) => {
              return volatePricing.volante_num === volante.volante_num;
            }
          );
          return { ...volante, pricing: { ...found, dam_num } };
        });

        const processArray = [{ ...dam, volantes }];
        console.log("process: ", processArray);

        setEstimateProcess(processArray);

        setDamStatus("SUCCESS");

        setDamNumnber(processArray[0].id);

        setDni(params.document_number);
      },
      onError: (error) => {
        const { modal_type } = error;
        handleError(modal_type, error);
      },
    }
  );

  const estimateDam = useAsyncExecutor2(
    (params: IEstimateDamData) => {
      const { dam_num, document_number, withdrawal_date } = params;
      const volante_nums = params.volantes.map((v) => v.volante_num);
      const requestDam = networkBackendRequest(
        `/dams/${dam_num}/estimate/intranet`,
        HTTP_METHODS.GET,
        {
          dam_num,
          volante_nums,
          withdrawal_date,
        }
      )
        .then((resp) => resp.json())
        .then(convertEstimateDamResponse(document_number));
      return from(requestDam).pipe();
    },
    {
      sequential: "SWITCH",
      retry: {
        interval: 1000,
        maxRetryAttempts: 1,
      },
      onProcessing: () => {
        setDamStatus("PROCESSING");
      },
      onSuccess: (dam: any, params) => {
        console.log("dam >: ", dam);
        if (dam.warning) {
          console.log(
            "dam.warning?.warning_block :",
            dam.warning?.warning_block
          );
          console.log(
            "dam.warning?.warning_message: ",
            dam.warning?.warning_message
          );
          setShowModalWarning(true);
          setMessageWarning(dam.warning?.warning_message);
        }
        if (!dam.warning || !dam.warning?.warning_block) {
          processEstimate.execute({ ...params, dam_response: dam });
          console.log("lanzado | processEstimate.execute");
        } else if (dam.warning?.warning_block === true) {
          setDamStatus("");
        }
      },
      onError: (error) => {
        const { modal_type } = error;
        setDamStatus("FAILED");
        setShowTypeModalError(modal_type ? modal_type : "general");
        const error_mesagge = getErrorValue(error);
        setErrors(error_mesagge);
      },
    }
  );

  useEffect(() => {
    updateVolantesForInvoice(selectedVolantes);
  }, [selectedVolantes]);

  const [damNumber, setDamNumnber] = useState("");
  const damSearchHandler = (
    damNumber: string,
    volanteNumbers: string[],
    params: { dni: string; date: Date }
  ) => {
    updateIsButtonDisabled(true);
    const volantes = volanteNumbers.map((volante_num) => {
      return {
        additional_services: [],
        volante_num,
      };
    });
    const damBody = {
      document_number: params.dni,
      withdrawal_date: format(params.date, "yyyy-MM-dd"),
      dam_num: damNumber,
      volantes,
    };
    estimateDam.execute(damBody);
  };

  const dateRucHook = useState({});
  const [dateRucStatus] = dateRucHook;

  const toggleModal = () => updateShowModal(!showModal);

  const checkCompleteFields = useCallback(() => {
    if (!volantesForInvoice.length) return false;
    let allFilled = true;
    volantesForInvoice.forEach((volante) => {
      // TODO: Review these conditions.
      if (!(volante.id in dateRucStatus)) {
        allFilled = false;
      } else if (
        !dateRucStatus[volante.id].amount ||
        dateRucStatus[volante.id].amount === "(No cotizado)"
      )
        allFilled = false;
      else if (
        !dateRucStatus[volante.id].billableRuc ||
        dateRucStatus[volante.id].billableRuc === "0"
      )
        allFilled = false;
    });

    return allFilled;
  }, [dateRucStatus, volantesForInvoice]);

  const onChangeVolantesForInvoice = useCallback(() => {
    updateIsButtonDisabled(!checkCompleteFields());
  }, [checkCompleteFields]);

  const [dateRuc] = dateRucHook;

  useEffect(() => {
    onChangeVolantesForInvoice();
  }, [volantesForInvoice, onChangeVolantesForInvoice, dateRuc]);

  const goToConfirmationScreen = () => {
    if (!checkCompleteFields) return;
    toggleModal();
  };

  const [damStatus, setDamStatus] = useState("");

  const dictionaryStatus = {
    "": REQUEST_STATES.PRE_REQUEST,
    PROCESSING: REQUEST_STATES.REQUEST_IN_PROGRESS,
  };

  return (
    <div>
      {showModalWarning && messageWarning && (
        <ConfirmationModal
          message={messageWarning}
          onAccept={() => {
            setMessageWarning("");
            setShowModalWarning(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            setMessageWarning("");
            setShowModalWarning(false);
          }}
        />
      )}

      {!isProd && <BatchPrefactura />}

      <BillingSearchBar
        searchHandlers={{ damSearchHandler }}
        searchError={searchError}
        updateSearchError={updateSearchError}
        requestStatus={dictionaryStatus[estimateDam.status]}
        dniState={dniState}
        volantesForRequestState={volantesForRequestState}
        updateDamSuffixState={updateDamSuffixState}
      />
      {(damStatus === "PROCESSING" || damStatus === "") && (
        <>
          <DamLoader damLoaderState={dictionaryStatus[damStatus]} />
        </>
      )}
      {damStatus === "SUCCESS" && (
        <>
          <>
            <StyledTag>
              <h4>PAGO DE FACTURACIÓN</h4>
            </StyledTag>
            <DivMessage>
              <div className="align-div-flex">
                <img
                  src={IconWarningBlack}
                  className="iconimage"
                  alt="warning"
                />
                <InstructionsLabel>
                  Seleccione los volantes a prefacturar, elija una fecha de
                  retiro (hasta 5 días después) y posteriormente elija un RUC
                  Facturable (Revisar dirección fiscal)
                </InstructionsLabel>
              </div>
            </DivMessage>

            <Dams
              dams={estimateProcess}
              dni={dni}
              updateSelectedVolantes={updateSelectedVolantes}
              dateRucHook={dateRucHook}
              searchError={searchError}
              onChangeVolantesForInvoice={onChangeVolantesForInvoice}
              refreshBilling={() => {
                setDni(INITIAL_FILTERS.dni);
                updateDamSuffix(INITIAL_FILTERS.damSuffix);
                updateVolantesForRequest(INITIAL_FILTERS.volantesForRequest);
                setDamStatus("");
                estimateDam.clearCache();
                processEstimate.clearCache();
              }}
            />

            <ButtonContainer>
              <Authorization actions={["quotation.create_quotations"]}>
                <MainButton
                  disabled={isButtonDisabled}
                  onClick={goToConfirmationScreen}
                >
                  {`Generar prefactura (${volantesForInvoice.length})`}
                </MainButton>
              </Authorization>
            </ButtonContainer>
          </>
        </>
      )}

      <Authorization actions={["quotation.create_quotations"]}>
        {showModal ? (
          <QuotationConfirmation
            dni={dni}
            toggleModal={toggleModal}
            volantes={volantesForInvoice}
            damId={damNumber}
            dateRucStatus={dateRucStatus}
            updateShowModalClose={updateShowModal}
            showModalClose={showModal}
            userInfo={userInfo}
            refreshBilling={() => {
              setDni(INITIAL_FILTERS.dni);
              updateDamSuffix(INITIAL_FILTERS.damSuffix);
              updateVolantesForRequest(INITIAL_FILTERS.volantesForRequest);
              updateShowModal(false);
              setDamStatus("");
              estimateDam.clearCache();
              processEstimate.clearCache();
            }}
          />
        ) : null}
      </Authorization>

      {damStatus === "FAILED" && showTypeModalError === "general" && (
        <>
          <ConfirmationModal
            message={errors}
            onAccept={() => {
              setDamStatus("");
              setErrors("");
              setShowTypeModalError("");
              updateSearchError(false);
            }}
            title={`Atención`}
            toggleModal={() => {
              setDamStatus("");
              setErrors("");
              setShowTypeModalError("");
              updateSearchError(false);
            }}
          />
        </>
      )}

      {damStatus === "FAILED" && showTypeModalError === "list" && (
        <ConfirmationModalList
          message={errors}
          onAccept={() => {
            setDamStatus("");
            setErrors("");
            setShowTypeModalError("");
            updateSearchError(false);
          }}
          title={`Hubo un error en tu búsqueda`}
          subTitle={"Estos son los resultados de los volantes de tu búsqueda."}
          toggleModal={() => {
            setDamStatus("");
            setErrors("");
            setShowTypeModalError("");
            updateSearchError(false);
          }}
        />
      )}
    </div>
  );
}

export default BillingScreen;
